import './AddInputModal.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import * as uuid from 'uuid';
import { Form, Modal } from 'react-bootstrap';
import { HiOutlinePlus } from 'react-icons/hi';
import { lessThanIgnoreTime } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { CrossChannelMediaIds } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Dropdown } from '@Components/Dropdown';
import { Checkbox, Radio } from '@Components/FormCheck';
import {
  Appearance,
  Background,
  Design,
  Intent,
  Placement,
  Size
} from '@Components/designConstants';
import { DateRange } from '../../../utils/date-utils';
import { useSetAreYouSure } from '../../../redux/modals';
import {
  createEmptyKpi,
  IncrementalityTestExperimentTypes,
  IncrementalityTestKpi,
  IncrementalityTestKpiType,
  IncrementalityTestMediaIdTypes,
  IncrementalityTestPortionTypes,
  IncrementalityTestExperimentLocations,
  validateKpis
} from './OfflineInputsUtils';
import { IncrementalityTestKpiComponent } from './IncrementalityTestKpiComponent';

interface AddIncrementalityTestProps {
  show: boolean;
  crossChannelMediaIds: CrossChannelMediaIds;
  handleClose: () => void;
  createTable: (
    name: string,
    hypothesis: string,
    testType: string,
    location: string,
    outcomeSource: string,
    outcomeSourceSubset: string,
    lagWindow: number,
    mediaIdType: string,
    mediaIds: string[],
    kpis: IncrementalityTestKpi[],
    startDate: Date,
    endDate: Date
  ) => void;
}

const lagWindowToString = (lagWindow: number): string => {
  const suffix = lagWindow > 1 ? 'Days' : 'Day';
  return `${lagWindow} ${suffix}`;
};

const LAG_WINDOW_OPTIONS = [1, 3, 7, 14, 30].map(ele => ({
  label: lagWindowToString(ele),
  value: ele.toString()
}));

const EXPERIMENT_NAME_MAX_LENGTH = 150;

export const AddIncrementalityTest = ({
  show,
  crossChannelMediaIds,
  handleClose,
  createTable
}: AddIncrementalityTestProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [experimentName, setExperimentName] = useState<string>('');
  const [experimentHypothesis, setExperimentHypothesis] = useState<string>('');
  const [experimentType, setExperimentType] = useState<
    IncrementalityTestExperimentTypes | undefined
  >(undefined);
  const [location, setLocation] = useState<IncrementalityTestExperimentLocations | undefined>(
    undefined
  );

  const [outcomeSource, setOutcomeSource] = useState<string>('');
  const [outcomeSourcePortionType, setOutcomeSourcePortionType] = useState<
    IncrementalityTestPortionTypes | undefined
  >(undefined);
  const [outcomeSourcePortionStr, setOutcomeSourcePortionStr] = useState<string>('');
  const [revenue, setRevenue] = useState<boolean>(false);
  const [lagWindow, setLagWindow] = useState<number | undefined>(undefined);
  const [dateRange, setDateRange] = useState<Partial<DateRange>>();
  const [kpiVolume, setKpiVolume] = useState<boolean>(false);
  const [kpis, setKpis] = useState<IncrementalityTestKpi[]>([createEmptyKpi()]);
  const [mediaIdType, setMediaIdType] = useState<IncrementalityTestMediaIdTypes | undefined>(
    undefined
  );
  const [mediaIds, setMediaIds] = useState<string[]>([]);
  const setAreYouSure = useSetAreYouSure();

  const resetState = useCallback(() => {
    setIsSubmitting(false);
    setExperimentName('');
    setExperimentHypothesis('');
    setExperimentType(undefined);
    setLocation(undefined);
    setOutcomeSource('');
    setOutcomeSourcePortionType(undefined);
    setOutcomeSourcePortionStr('');
    setRevenue(false);
    setLagWindow(undefined);
    setKpiVolume(false);
    setKpis([createEmptyKpi()]);
    setMediaIdType(undefined);
    setMediaIds([]);
    setDateRange(undefined);
  }, []);

  const isSubmitDisabled = useMemo(
    () =>
      isSubmitting ||
      !experimentName ||
      !experimentType ||
      !location ||
      !outcomeSource ||
      !outcomeSourcePortionType ||
      !outcomeSourcePortionStr ||
      !lagWindow ||
      !mediaIdType ||
      !mediaIds.length ||
      !validateKpis(kpis, revenue, kpiVolume) ||
      !dateRange?.start ||
      !dateRange?.end ||
      lessThanIgnoreTime(new Date(dateRange.end), new Date(dateRange.start), false),
    [
      isSubmitting,
      experimentName,
      experimentType,
      location,
      outcomeSource,
      outcomeSourcePortionType,
      outcomeSourcePortionStr,
      lagWindow,
      mediaIdType,
      mediaIds,
      revenue,
      kpiVolume,
      kpis,
      dateRange
    ]
  );

  const createTableKpis = useCallback(
    (allKpis: IncrementalityTestKpi[]) => {
      const lastKpiIdx = kpiVolume ? allKpis.length : 1;
      return allKpis.slice(0, lastKpiIdx);
    },
    [kpiVolume]
  );

  const updateKpiEntry = useCallback((kpiUuid: string, value: string) => {
    setKpis(prev => prev.map(kpi => (kpi.uuid === kpiUuid ? { ...kpi, name: value } : kpi)));
  }, []);

  const deleteKpiEntry = useCallback(
    (kpiUuid: string, name: string) => {
      setAreYouSure({
        title: `Delete ${name}`,
        message: `You are about to delete the ${name} KPI, are you sure?`,
        intent: Intent.ERROR,
        promise: false,
        cancelText: 'Never mind',
        okayText: 'Yes',
        onOkay: () => {
          setKpis(prev => prev.filter(kpi => kpi.uuid !== kpiUuid));
        }
      });
    },
    [setAreYouSure]
  );

  useEffect(() => {
    const volumeKpis = kpis.filter(
      kpi => kpi.type !== IncrementalityTestKpiType.REVENUE && kpi.name.length > 0
    );
    if (volumeKpis.length) {
      setKpiVolume(true);
    }
  }, [kpis]);

  return (
    <Modal
      className='add-input-modal add-incrementality-test-modal'
      show={show}
      onHide={() => {
        handleClose();
        resetState();
      }}
      size='xl'
      centered
    >
      <Modal.Header>
        <h4 className='modal-title'>Add Incrementality Testing Experiment Results</h4>
        <div className='sub-title'>*Required fields marked with an asterisk.</div>
      </Modal.Header>
      <Modal.Body>
        <div
          className='incrementality-test-column'
          id='incrementality-test-name-and-hypothesis-col'
        >
          <div className='experiment-name-input'>
            <div className='section-title'>
              Incrementality Test Name<span className='asterisk'>*</span>
            </div>
            <Form.Control
              as='textarea'
              className='textarea'
              placeholder="Ex: Youtube Conversion Lift Q3 '23"
              maxLength={EXPERIMENT_NAME_MAX_LENGTH}
              value={experimentName}
              onChange={e => setExperimentName(e.target.value)}
            />
            <div className='input-explanation'>
              <div
                className={cn('explanation', {
                  error: experimentName.length === EXPERIMENT_NAME_MAX_LENGTH
                })}
              >
                <p>
                  {experimentName.length === EXPERIMENT_NAME_MAX_LENGTH
                    ? 'Incrementality Test Name must be less than or equal to 150 characters.'
                    : 'Short title that names the channel / tactic so results are easy to find later.'}
                </p>
              </div>
            </div>
          </div>
          <div className='experiment-hypothesis-input'>
            <div className='section-title'>
              Experiment Hypothesis <span className='asterisk'>*</span>
            </div>
            <Form.Control
              as='textarea'
              className='textarea'
              placeholder='Ex:  Increasing youtube spend drives more conversions.'
              value={experimentHypothesis}
              onChange={e => setExperimentHypothesis(e.target.value)}
            />
            <div className='input-explanation'>
              <div className='explanation'>
                <p>
                  Clear statement of what you expected to learn, including audience, action, and
                  outcome.
                </p>
              </div>
            </div>
          </div>
          <div className='time-frame' id='incrementality-test-time-frame'>
            <div className='section-title'>
              Time Frame<span className='asterisk'>*</span>
            </div>
            <div className='top-container'>
              <div className='date-container'>
                <DatePicker
                  background={Background.LIGHT}
                  range={
                    dateRange?.start && dateRange?.end
                      ? { start: dateRange.start, end: dateRange.end }
                      : undefined
                  }
                  design={Design.BRAND}
                  onChange={newDates => setDateRange(newDates)}
                  appearance={Appearance.FILLED}
                  removeHeaderText
                />
              </div>
              <div className='input-explanation'>
                <div className='explanation'>
                  <p>
                    Period when the test ran. Ensure start/end dates match the live test window.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='incrementality-test-column'>
          <div className='experiment-type'>
            <div className='section-title'>
              Incrementality Test Type<span className='asterisk'>*</span>
            </div>
            <div className='top-radios'>
              <div id='geo-test-container'>
                <Radio
                  label={IncrementalityTestExperimentTypes.GEO}
                  checked={experimentType === IncrementalityTestExperimentTypes.GEO}
                  id='geo'
                  onChange={() => setExperimentType(IncrementalityTestExperimentTypes.GEO)}
                />
                <div className='input-explanation'>
                  <div className='explanation'>
                    <p>
                      Splits regions into test vs. control. Best when platform can&apos;t randomize
                      users.
                    </p>
                  </div>
                </div>
              </div>
              <div id='user-test-container'>
                <Radio
                  label={IncrementalityTestExperimentTypes.USER}
                  checked={experimentType === IncrementalityTestExperimentTypes.USER}
                  id='user'
                  onChange={() => setExperimentType(IncrementalityTestExperimentTypes.USER)}
                />
                <div className='input-explanation'>
                  <div className='explanation'>
                    <p>
                      Splits users into test vs. control at random (often platform-driven). Best for
                      targeted tactics.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className='input-explanation note'>
              <div className='explanation'>
                <p>
                  Note: If your test type isn&apos;t listed here, please contact your Tinuiti
                  Econometrics partner for setup assistance.
                </p>
              </div>
            </div>
          </div>
          <div className='location'>
            <div className='section-title'>
              Test Region<span className='asterisk'>*</span>
            </div>
            <div className='location-container'>
              <div className='location-dropdown'>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}

                  value={location}
                  options={[
                    IncrementalityTestExperimentLocations.US,
                    IncrementalityTestExperimentLocations.CA,
                    IncrementalityTestExperimentLocations.EU
                  ]}
                  onChange={setLocation}
                  size={Size.LG}
                  placeholder='Select Region'
                />
              </div>
              <div id='location-explanation' className='input-explanation'>
                <div className='explanation'>
                  <p>Choose the region where the experiment took place.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='incrementality-test-column' id='outcome-source-lag-and-var-col'>
          <div className='outcome-source'>
            <div className='outcome-source-input-container'>
              <div className='section-title'>
                Outcome Source<span className='asterisk'>*</span>
              </div>
              <Form.Control
                placeholder='Ex: DTC'
                value={outcomeSource}
                onChange={e => setOutcomeSource(e.target.value)}
              />
              <div className='input-explanation'>
                <div className='explanation'>
                  <p>
                    Tell us where conversions are coming from — whether total business results or a
                    specific stream (ex: DTC, retail, or a single vertical).
                  </p>
                </div>
              </div>
            </div>
            <div className='outcome-source-portion'>
              <div id='total-radio-container'>
                <Radio
                  label={`${IncrementalityTestPortionTypes.TOTAL} ${outcomeSource}`}
                  checked={outcomeSourcePortionType === IncrementalityTestPortionTypes.TOTAL}
                  id='total'
                  onChange={() => {
                    setOutcomeSourcePortionType(IncrementalityTestPortionTypes.TOTAL);
                    setOutcomeSourcePortionStr(IncrementalityTestPortionTypes.TOTAL);
                  }}
                />
                <div className='input-explanation'>
                  <div className='explanation'>
                    <p>Measure across all users/events. Use when looking at broad impact.</p>
                  </div>
                </div>
              </div>
              <div id='subset-radio-container'>
                <Radio
                  label={`${IncrementalityTestPortionTypes.SUBSET} of ${outcomeSource}`}
                  checked={outcomeSourcePortionType === IncrementalityTestPortionTypes.SUBSET}
                  id='subset'
                  onChange={() => {
                    setOutcomeSourcePortionType(IncrementalityTestPortionTypes.SUBSET);
                    setOutcomeSourcePortionStr('');
                  }}
                />
                <div className='input-explanation'>
                  <div className='explanation'>
                    <p>
                      Measure a specific segment of interest (ex: new users, product category,
                      repeat buyers).
                    </p>
                  </div>
                </div>
                <div className='outcome-source-subset-input'>
                  <div className='subset-input-label'>Please Specify Subset</div>
                  <Form.Control
                    placeholder='Ex: New Users'
                    value={outcomeSourcePortionStr}
                    onChange={e => setOutcomeSourcePortionStr(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='lag-window-input'>
            <div className='section-title'>
              Lag Window<span className='asterisk'>*</span>
            </div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}

              value={lagWindow === undefined ? '' : String(lagWindow)}
              options={LAG_WINDOW_OPTIONS}
              onChange={val => {
                setLagWindow(Number.parseInt(val, 10));
              }}
              size={Size.LG}
              placeholder='Lag Window'
            />
          </div>
          <div className='outcome-variable-input'>
            <div className='section-title'>
              Outcome Variable<span className='asterisk'>*</span>
            </div>
            <div className='outcome-variable-checkboxes'>
              <Checkbox
                label='Revenue'
                checked={revenue}
                id='revenue'
                onChange={() => {
                  if (revenue) {
                    setKpis(prev =>
                      prev.filter(kpi => kpi.type !== IncrementalityTestKpiType.REVENUE)
                    );
                  } else {
                    setKpis(prev => [
                      {
                        uuid: uuid.v4(),
                        type: IncrementalityTestKpiType.REVENUE,
                        name: 'revenue'
                      },
                      ...prev
                    ]);
                  }
                  setRevenue(!revenue);
                }}
              />
              <div className='kpi-volume-checkbox'>
                <Checkbox
                  label='KPI Volume'
                  checked={kpiVolume}
                  id='kpiVolume'
                  onChange={() => {
                    if (kpiVolume) {
                      setKpis([
                        ...kpis.filter(kpi => kpi.type !== IncrementalityTestKpiType.VOLUME),
                        createEmptyKpi()
                      ]);
                    }
                    setKpiVolume(!kpiVolume);
                  }}
                />
                {kpiVolume && (
                  <div className='input-explanation'>
                    <div className='explanation'>
                      <p>Note: use a high-level / aggregate KPI (ex: Leads, Purchases)</p>
                    </div>
                  </div>
                )}
              </div>
              {kpiVolume && (
                <div className='outcome-variable-kpis'>
                  {kpis
                    .filter(kpi => kpi.type !== IncrementalityTestKpiType.REVENUE)
                    .map((kpi, index) => (
                      <IncrementalityTestKpiComponent
                        key={kpi.uuid}
                        index={index}
                        kpi={kpi}
                        updateKpi={updateKpiEntry}
                        deleteKpi={deleteKpiEntry}
                      />
                    ))}
                  <div id='add-kpi-button'>
                    <Button
                      size={Size.LG}
                      disabled={isSubmitting}
                      onClick={() => setKpis(prev => [...prev, createEmptyKpi()])}
                    >
                      <HiOutlinePlus />
                      Add KPI
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='incrementality-test-column' id='incrementality-test-media-id-col'>
          <div className='media-id-container'>
            <div className='section-title'>
              Test Placement<span className='asterisk'>*</span>
            </div>
            <div className='media-id-input-body'>
              <div id='accountIdContainer'>
                <Radio
                  label='Account'
                  checked={mediaIdType === IncrementalityTestMediaIdTypes.ACCOUNT_ID}
                  id='accountId'
                  onChange={() => {
                    setMediaIdType(IncrementalityTestMediaIdTypes.ACCOUNT_ID);
                    setMediaIds([]);
                  }}
                />
                {mediaIdType === IncrementalityTestMediaIdTypes.ACCOUNT_ID && (
                  <div className='account-id-select-container'>
                    <div className='select-label'>Account ID</div>
                    <Dropdown
                      searchable
                      clearable
                      className='select'
                      placeholder='Ex: XXXXXXXXX'
                      options={crossChannelMediaIds.accountIds}
                      value={mediaIds[0] ?? null}
                      onChange={accountId => setMediaIds(accountId ? [accountId] : [])}
                      reactSelectProps={{ isLoading: crossChannelMediaIds.accountIds.length === 0 }}
                    />
                  </div>
                )}
              </div>
              <div id='campaignIdContainer'>
                <Radio
                  label='Campaigns'
                  checked={mediaIdType === IncrementalityTestMediaIdTypes.CAMPAIGN_ID}
                  id='campaignId'
                  onChange={() => {
                    setMediaIdType(IncrementalityTestMediaIdTypes.CAMPAIGN_ID);
                    setMediaIds([]);
                  }}
                />
                {mediaIdType === IncrementalityTestMediaIdTypes.CAMPAIGN_ID && (
                  <div className='campaign-id-select-container'>
                    <div className='select-label'>Campaign ID(s)</div>
                    <Dropdown
                      searchable
                      multiSelect
                      showChips
                      clearable
                      className='select'
                      placeholder='Ex: XXXXXXXXX'
                      options={crossChannelMediaIds.campaignIds}
                      value={mediaIds}
                      onChange={setMediaIds}
                      reactSelectProps={{
                        isLoading: crossChannelMediaIds.campaignIds.length === 0
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={() => {
            handleClose();
            resetState();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            if (
              isSubmitting ||
              !experimentName ||
              !experimentType ||
              !location ||
              !outcomeSource ||
              !outcomeSourcePortionType ||
              !outcomeSourcePortionStr ||
              !lagWindow ||
              !mediaIdType ||
              !mediaIds.length ||
              !validateKpis(kpis, revenue, kpiVolume) ||
              !dateRange?.start ||
              !dateRange?.end ||
              lessThanIgnoreTime(new Date(dateRange.end), new Date(dateRange.start), false)
            ) {
              return;
            }
            setIsSubmitting(true);
            await createTable(
              experimentName,
              experimentHypothesis,
              experimentType,
              location,
              outcomeSource,
              outcomeSourcePortionStr,
              lagWindow,
              mediaIdType,
              mediaIds,
              createTableKpis(kpis),
              new Date(dateRange.start),
              new Date(dateRange.end)
            );
            handleClose();
            resetState();
          }}
          disabled={isSubmitDisabled}
          loading={isSubmitting}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
